import * as THREE from "three";
import type { FloorplannerStore } from "../../store/floorplannerStore";
import type { EditorStoreType } from "../../store/editorStore";
import {
  FillPattern,
  isAreaFillSymbolType,
  isWallType,
} from "../../types/floorplannerTypes";
import { getPlanBoundsFromWalls } from "../floorplan3d/geometry";
import { buildRoomWallsFromTemplate } from "./buildRoomWalls";
import { getRoomFloorColor, RoomFloorColorId } from "./roomFloorColors";
import { getRoomWallColor, RoomWallColorId } from "./roomWallColors";
import { RoomShapeId, RoomShapeParams } from "./roomShapeTemplates";

/**
 * Seed a closed room from a shape template into the floorplan, then switch
 * into 3D view focused on the new room. If walls already exist, the new room
 * is placed to the right.
 */
export async function seedRoomFromTemplate(options: {
  floorplannerStore: FloorplannerStore;
  editorStore: EditorStoreType;
  shapeId: RoomShapeId;
  params: RoomShapeParams;
  /** Optional floor fill pattern applied to newly created area fill(s). */
  floorPattern?: FillPattern;
  /** Floor fill color preset applied to newly created area fill(s). */
  floorColorId?: RoomFloorColorId;
  /** Wall fill color preset. */
  wallColorId?: RoomWallColorId;
}): Promise<{ wallIds: string[]; blocked?: boolean }> {
  const {
    floorplannerStore,
    editorStore,
    shapeId,
    params,
    floorPattern,
    floorColorId,
    wallColorId,
  } = options;

  const existingAreaFillIds = new Set(
    Array.from(floorplannerStore.symbolsMap.values())
      .filter(isAreaFillSymbolType)
      .map((symbol) => symbol.id),
  );

  const fillColor = wallColorId
    ? getRoomWallColor(wallColorId).color
    : undefined;

  const { walls, wallIds } = buildRoomWallsFromTemplate({
    shapeId,
    params,
    generateId: () => floorplannerStore.generateId(),
    wallWidth: floorplannerStore.wallWidth,
    fillColor,
  });

  // Place beside existing geometry so "Add room" does not stack on top.
  if (floorplannerStore.wallsMap.size > 0) {
    let maxX = -Infinity;
    floorplannerStore.wallsMap.forEach((wall) => {
      maxX = Math.max(maxX, wall.start.x, wall.end.x);
    });
    if (Number.isFinite(maxX)) {
      let newMinX = Infinity;
      for (const wall of walls) {
        newMinX = Math.min(newMinX, wall.start.x, wall.end.x);
      }
      const offsetX = maxX - newMinX + 1; // 1 m gap
      const offset = new THREE.Vector2(offsetX, 0);
      for (const wall of walls) {
        wall.start.add(offset);
        wall.end.add(offset);
      }
    }
  }

  // Freemium gate: reject the whole room if walls + the area fill room detection
  // will create would exceed the per-floorplan shape cap. Matches paste's
  // all-or-nothing check so we never seed a partial polygon then save/3D.
  const shapesNeeded = walls.length + 1;
  if (!floorplannerStore.canAddShape(shapesNeeded)) {
    editorStore.showPaywall("shapes");
    return { wallIds: [], blocked: true };
  }

  // Group walls + auto-created room/area-fill into one undo entry so Undo
  // removes the whole seeded room instead of one wall at a time.
  const existingRoomIds = new Set(floorplannerStore.roomsMap.keys());
  floorplannerStore.beginUndoTransaction("addRoom");
  floorplannerStore.setDeferRoomUpdates(true);
  try {
    for (const wall of walls) {
      floorplannerStore.addWall(wall, { commitUndo: false });
    }
  } finally {
    floorplannerStore.setDeferRoomUpdates(false);
  }

  floorplannerStore.initializeAllRooms();

  const floorFillColor = floorColorId
    ? getRoomFloorColor(floorColorId).color
    : undefined;

  // Apply floor choices to new area fills. Explicitly set fillPattern (including
  // undefined for "None") so a prior default cannot linger.
  floorplannerStore.symbolsMap.forEach((symbol, id) => {
    if (!isAreaFillSymbolType(symbol)) return;
    if (existingAreaFillIds.has(id)) return;
    floorplannerStore.symbolsMap.set(id, {
      ...symbol,
      fillPattern: floorPattern,
      ...(floorFillColor !== undefined ? { fillColor: floorFillColor } : {}),
    });
  });

  // Room detection and floor styling write maps directly; record them so Undo
  // clears rooms and area fills along with the walls.
  floorplannerStore.roomsMap.forEach((_, id) => {
    if (!existingRoomIds.has(id)) {
      floorplannerStore.recordRoomChange(id, "add");
    }
  });
  floorplannerStore.symbolsMap.forEach((symbol, id) => {
    if (!isAreaFillSymbolType(symbol)) return;
    if (existingAreaFillIds.has(id)) return;
    floorplannerStore.recordSymbolChange(id, "add");
  });

  floorplannerStore.pushToUndoStack();
  floorplannerStore.setDirty();

  await floorplannerStore.saveToCloud(false, true);

  // Frame the new room in 3D (not the whole existing plan).
  const createdWalls = wallIds
    .map((id) => floorplannerStore.wallsMap.get(id))
    .filter(isWallType);
  const focusBounds =
    createdWalls.length > 0 ? getPlanBoundsFromWalls(createdWalls) : null;
  if (createdWalls.length > 0) {
    editorStore.setSelections(createdWalls);
  }

  editorStore.setView3D(true);
  editorStore.request3DResetZoom(focusBounds);

  return { wallIds };
}
